import React, { useEffect } from 'react';
import type { WidgetConfig } from './types';
import { useWidgetAuth } from './WidgetAuthService';

interface WidgetChatContentProps {
    config: WidgetConfig;
}

/**
 * Chat content component that renders inside the modal
 * Embeds the Atlas Frontend in an iframe with the authentication token
 */
export const WidgetChatContent: React.FC<WidgetChatContentProps> = ({ config }) => {
    const { isAuthenticated, isLoading, token, login } = useWidgetAuth();
    const iframeRef = React.useRef<HTMLIFrameElement>(null);

    if (isLoading) {
        return (
            <div className="atlas-widget-chat-loading">
                <div className="atlas-widget-loading__spinner" />
                <span>Loading...</span>
            </div>
        );
    }

    if (!isAuthenticated || !token) {
        return (
            <div className="atlas-widget-chat-loading">
                <span>You are not logged in. Please log in to use Atlas AI.</span>
                <button onClick={login}>Log in</button>
            </div>
        );
    }

    // Get the Atlas Frontend URL from the widget script location
    // The widget is served from the frontend, so we can derive the base URL from the script src
    const getAtlasFrontendUrl = (): string => {
        const scripts = document.getElementsByTagName('script');
        for (let i = 0; i < scripts.length; i++) {
            const src = scripts[i].src;
            if (src && src.includes('atlas-widget')) {
                // Extract origin from script URL (e.g., https://localhost:49976)
                const url = new URL(src);
                return url.origin;
            }
        }
        // Fallback: try to derive from API URL (remove /api)
        return config.apiUrl.replace(/\/api\/?$/, '');
    };

    const atlasBaseUrl = getAtlasFrontendUrl();
    const atlasUrl = `${atlasBaseUrl}?embedded=true`;

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) {
            return;
        }

        iframe.addEventListener('load', () => {
            console.log('Iframe loaded, sending application metadata');

            // Send the token to the iframe via postMessage
            iframe.contentWindow?.postMessage(
                { applicationMetadata: config.applicationMetadata },
                '*'
            );
        });
    }, []);

    return (
        <div className="atlas-widget-chat">
            <iframe
                ref={iframeRef}
                id="atlas-iframe"
                src={atlasUrl}
                className="atlas-widget-iframe"
                title="Atlas Assistant"
                allow="clipboard-write"
            />
        </div>
    );
};
